import { useMutation } from '@tanstack/react-query';
import { ImagePlus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { api, type Asset } from '../api/client';
import { ErrorBox, Field } from './ui';

/**
 * Picture field. "drop" (the Live TV mockups): a dashed box "Click to upload" that also takes a
 * dropped file, and once set shows the picture with a delete button. "row": preview + Choose file.
 * Uploads straight away.
 */
export function ImagePicker({
  label,
  value,
  onChange,
  hint,
  dark = true,
  wide = false,
  variant = 'row',
  height,
}: {
  label: string;
  value: string | null;
  onChange: (a: Asset | null) => void;
  hint?: string;
  /** Dark backdrop behind the preview (logos are usually light). */
  dark?: boolean;
  /** 16:9 preview for photos. */
  wide?: boolean;
  variant?: 'row' | 'drop';
  height?: number;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const upload = useMutation({ mutationFn: (f: File) => api.upload<Asset>('/assets', f), onSuccess: onChange });
  const pick = () => input.current?.click();
  const fileInput = (
    <input
      ref={input}
      type="file"
      accept="image/png,image/jpeg,image/webp"
      hidden
      onChange={(e) => {
        const f = e.target.files?.[0];
        if (f) upload.mutate(f);
        e.target.value = '';
      }}
    />
  );

  if (variant === 'drop') {
    return (
      <Field label={label} hint={hint}>
        {value ? (
          <div className={`drop-filled${dark ? ' dark' : ''}${wide ? ' wide' : ''}`} style={height ? { height } : undefined}>
            <img src={value} alt="" style={{ objectFit: wide ? 'cover' : 'contain' }} onClick={pick} title="Replace" />
            <button type="button" className="drop-remove" onClick={() => onChange(null)} title="Remove">
              <Trash2 size={14} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`dropzone${over ? ' over' : ''}`}
            style={height ? { height } : undefined}
            disabled={upload.isPending}
            onClick={pick}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) upload.mutate(f);
            }}
          >
            <ImagePlus size={22} />
            <span className="dropzone-title">{upload.isPending ? 'Uploading…' : 'Click to upload'}</span>
            <span className="dropzone-hint">PNG, JPG or WebP, up to 8 MB</span>
          </button>
        )}
        {fileInput}
        <ErrorBox error={upload.error} />
      </Field>
    );
  }

  return (
    <Field label={label} hint={hint}>
      <div className="picker">
        <div className={`picker-preview${wide ? ' wide' : ''}${dark ? ' dark' : ''}`}>
          {value ? <img src={value} alt="" style={{ objectFit: wide ? 'cover' : 'contain' }} /> : <ImagePlus size={20} />}
        </div>
        <button type="button" className="btn small" disabled={upload.isPending} onClick={pick}>
          {upload.isPending ? 'Uploading…' : 'Choose file'}
        </button>
        {value && (
          <button type="button" className="btn small" onClick={() => onChange(null)}>
            Remove
          </button>
        )}
        {fileInput}
      </div>
      <ErrorBox error={upload.error} />
    </Field>
  );
}
